/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.21.0
 * @since 1.15.0
 */
import { ETextBlockStatus } from '../../components/common/TextBlockStatus/textBlockStatusTypes';
import { IPageOrderIdTranslation } from '../../modules/internationalization/types';
import { ECalendarPeriod } from '../../modules/internationalization/types/common/calendar';


/*
* -------------------------------------------
*               API
* -------------------------------------------
*/

export interface IOrderIdQuery {
    orderId: string;
}

/*
* -------------------------------------------
*               ORDER ID INFO
* -------------------------------------------
*/

export enum EOrderIdStatus {
    confirm = 'CONFIRM',
    confirmedPartially = 'CONFIRMED_PARTIALLY',
    error = 'ERROR',
    processing = 'PROCESSING',
    rejected = 'REJECTED',
    reorder = 'REORDER',
    success = 'SUCCESS',
    successPartially = 'SUCCESS_PARTIALLY',
    waitingConfirm = 'WAITING_CONFIRM',
    waitingProcessing = 'WAITING_PROCESSING',
}

export enum EOrderIdParameterAdditions {
    additionalAccount = 'ADDITIONAL_ACCOUNT',   // Дополнительные счета
    dcRuleset = 'DCRULESET',                    // Набор правил
    externalService = 'EXTERNAL_SERVICE',       // Услуги
    favDirect = 'FAVDIRECT',                    // Любимые направления
    rateOption = 'RATEOPTION',                  // Тарифные опции
    ratePlan = 'RATEPLAN',                      // Тарифный план
    rateService = 'RATESERVICE',                // Услуги и тарифные опции
    service = 'SERVICE',                        // Услуги
}

export enum EOrderIdParameterType {
    autoReport = 'AUTO_REPORT',
    date = 'DATE',
    datePeriod = 'DATE_PERIOD',
    link = 'LINK',
    list = 'LIST',
    text = 'TEXT', // common
}

export interface IOrderIdParameterCommon {
    value: string;
    type?: EOrderIdParameterType; // default EOrderIdParameterType.text
}

export interface IOrderIdParameterDate extends IOrderIdParameterCommon {
    isCurrent: boolean;
}

export interface IOrderIdParameterLink extends IOrderIdParameterCommon {
    id: string;
    isExternal: boolean;
}

export interface IOrderIdParameterDatePeriod {
    type?: EOrderIdParameterType; // default EOrderIdParameterType.text
    valueFrom: string;
    valueTo: string;
}

export interface IOrderIdParameterAutoReport extends IOrderIdParameterCommon {
    dayOfPeriod: number;            // 1-Пн, 2-Вт, 3-Ср, 4-Чт, 5-Пт, 6-Сб, 7-Вс
    delDate: string;
    periodType: ECalendarPeriod;    // Тип периода: Ежедневно, Еженедельно, Ежемесячно
    reportId: string;
    startTime: string;
}

export interface IOrderIdParameterList {
    value: Array<IOrderIdParameterCommon | IOrderIdParameterLink>;
    type?: EOrderIdParameterType;
}

export type TOrderIdParamType = IOrderIdParameterCommon
    | IOrderIdParameterAutoReport
    | IOrderIdParameterDate
    | IOrderIdParameterDatePeriod
    | IOrderIdParameterLink
    | IOrderIdParameterList
    ;

export type IOrderIdInfoParameters =
    { [key in keyof IPageOrderIdTranslation['parameters']['fields']]?: TOrderIdParamType }

export interface IOrderIdInfo {
    count: number;
    actionId: number;
    date: string;
    name: string;
    status: EOrderIdStatus;
    user: string;

    parameters: Partial<IOrderIdInfoParameters>;

    exports?: string[];
    subsCountWithErrors?: number;
    isShowSubsList: boolean;

    files: IOrderIdFile;
}

/*
* -------------------------------------------
*           ORDER ID SUBSCRIBER LIST
* -------------------------------------------
*/

export interface IOrderIdSubscriber {
    msisdn: string;
    id: number;

    name?: string;
    label?: string;

    statusType: ETextBlockStatus;
    statusMessage?: string;
    statusDescription?: string;
}

/*
* -------------------------------------------
*               ORDER ID FILE
* -------------------------------------------
*/

export interface IOrderIdFile {
    email: string; // Предустановленный email для модального окна
    files: Array<{ fileName: string; fileId: number }>;
    isCdmReport: boolean;
    isReportNotAvailable: boolean;
}

export interface IOrderIdFileModal {
    email: string;
    zip: boolean;
    zipPasswordOptional: boolean;
    zipPassword: string;
}

export enum EOrderIdFileModalField {
    email = 'email',
    zip = 'zip',
    zipPasswordOptional = 'zipPasswordOptional',
    zipPassword = 'zipPassword',
}
